/**
 * Short proposal links for plain-text channels. A text message or plain-text
 * email cannot hide a URL behind link text, so the URL itself has to be short:
 * `quote.blissbunkbeds.com/p/<code>` instead of a 36-character UUID plus query
 * string. The code is the quote id packed into base64url, so links resolve with
 * no lookup table and no round trip to the database.
 *
 * `api/lib/blissShortLinks.ts` is a byte-identical copy for the serverless
 * functions, which cannot import from `src/` — keep both files in sync.
 */

const QUOTE_HOST = 'https://quote.blissbunkbeds.com'

export const BLISS_SHORT_LINK_PREFIX = '/p'

/** Which part of the proposal the link opens. */
export type BlissShortLinkTarget = 'proposal' | 'approve' | 'design' | 'consult'

export type BlissShortLinkOptions = {
  target?: BlissShortLinkTarget
  /** Show the paid-consultation credit on the proposal. */
  consultCreditPaid?: boolean
}

const TARGET_FLAG: Record<BlissShortLinkTarget, string> = {
  proposal: '',
  approve: 'a',
  design: 'd',
  consult: 'c',
}

const TARGET_QUERY: Record<BlissShortLinkTarget, string> = {
  proposal: '',
  approve: 'step=approve',
  design: 'offer=design_package',
  consult: 'offer=room_review',
}

const CREDIT_FLAG = 'k'
const CREDIT_QUERY = 'consultCredit=1'

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
const CODE_LENGTH = 22
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function uuidToBytes(quoteRequestId: string): number[] | null {
  if (!UUID_PATTERN.test(quoteRequestId)) return null
  const hex = quoteRequestId.replace(/-/g, '').toLowerCase()
  const bytes: number[] = []
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.slice(i, i + 2), 16))
  }
  return bytes
}

function bytesToBase64Url(bytes: number[]): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]
    const b1 = bytes[i + 1]
    const b2 = bytes[i + 2]
    out += B64_CHARS[b0 >> 2]
    out += B64_CHARS[((b0 & 0b11) << 4) | ((b1 ?? 0) >> 4)]
    if (b1 === undefined) break
    out += B64_CHARS[((b1 & 0b1111) << 2) | ((b2 ?? 0) >> 6)]
    if (b2 === undefined) break
    out += B64_CHARS[b2 & 0b111111]
  }
  return out
}

function base64UrlToHex(code: string): string | null {
  const bits: number[] = []
  for (const char of code) {
    const value = B64_CHARS.indexOf(char)
    if (value < 0) return null
    bits.push(value)
  }
  const bytes: number[] = []
  for (let i = 0; i < bits.length; i += 4) {
    const [c0, c1, c2, c3] = [bits[i], bits[i + 1], bits[i + 2], bits[i + 3]]
    if (c1 === undefined) return null
    bytes.push(((c0 << 2) | (c1 >> 4)) & 0xff)
    if (c2 === undefined) break
    bytes.push(((c1 << 4) | (c2 >> 2)) & 0xff)
    if (c3 === undefined) break
    bytes.push(((c2 << 6) | c3) & 0xff)
  }
  if (bytes.length !== 16) return null
  return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/** 22-character code for a quote id, or null when the id is not a UUID. */
export function encodeBlissQuoteCode(
  quoteRequestId: string,
  options: BlissShortLinkOptions = {},
): string | null {
  const bytes = uuidToBytes(quoteRequestId.trim())
  if (!bytes) return null
  const flags = `${TARGET_FLAG[options.target || 'proposal']}${options.consultCreditPaid ? CREDIT_FLAG : ''}`
  return `${bytesToBase64Url(bytes)}${flags}`
}

export type BlissShortLinkCode = {
  quoteRequestId: string
  target: BlissShortLinkTarget
  consultCreditPaid: boolean
}

export function decodeBlissQuoteCode(code: string): BlissShortLinkCode | null {
  const trimmed = code.trim()
  if (trimmed.length < CODE_LENGTH) return null
  const hex = base64UrlToHex(trimmed.slice(0, CODE_LENGTH))
  if (!hex) return null
  const flags = trimmed.slice(CODE_LENGTH)
  const targetEntry = (Object.entries(TARGET_FLAG) as [BlissShortLinkTarget, string][]).find(
    ([, flag]) => flag && flags.includes(flag),
  )
  return {
    quoteRequestId: [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-'),
    target: targetEntry ? targetEntry[0] : 'proposal',
    consultCreditPaid: flags.includes(CREDIT_FLAG),
  }
}

function longProposalPath(quoteRequestId: string, options: BlissShortLinkOptions): string {
  const query = [
    options.consultCreditPaid ? CREDIT_QUERY : '',
    TARGET_QUERY[options.target || 'proposal'],
  ].filter(Boolean)
  const base = `/quote-approve/${encodeURIComponent(quoteRequestId.trim())}`
  return query.length ? `${base}?${query.join('&')}` : base
}

/**
 * Where `/p/<code>` sends the customer, or null when the code is unreadable.
 */
export function blissShortLinkDestination(code: string): string | null {
  const decoded = decodeBlissQuoteCode(code)
  if (!decoded) return null
  return longProposalPath(decoded.quoteRequestId, {
    target: decoded.target,
    consultCreditPaid: decoded.consultCreditPaid,
  })
}

/**
 * The link to put in a text message or plain-text email. Falls back to the full
 * approval URL for non-UUID ids, and to the funnel when there is no quote yet.
 */
export function blissProposalShortLink(
  quoteRequestId?: string | null,
  options: BlissShortLinkOptions = {},
): string {
  const id = quoteRequestId?.trim()
  if (!id) return `${QUOTE_HOST}/?funnel_step=estimate`
  const code = encodeBlissQuoteCode(id, options)
  return code
    ? `${QUOTE_HOST}${BLISS_SHORT_LINK_PREFIX}/${code}`
    : `${QUOTE_HOST}${longProposalPath(id, options)}`
}

/** Same link without the scheme, for print and PDF where nothing is clickable. */
export function blissProposalShortLinkLabel(
  quoteRequestId?: string | null,
  options: BlissShortLinkOptions = {},
): string {
  return blissProposalShortLink(quoteRequestId, options).replace(/^https:\/\//, '')
}
